import React, { useCallback, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Activity, AlertTriangle, Eye, Users } from 'lucide-react';
import {
  TaskyAlert,
  TaskyBadge,
  TaskyButton,
  TaskyEmptyState,
  TaskyErrorState,
  TaskySpinner,
  TaskyStatCard,
} from '../../tasky';
import adminBehaviorService, {
  BehaviorDailyReport,
  BehaviorDailyReportStatus,
  BehaviorDailyReportSummary,
} from '../../../services/api/adminBehaviorService';
import { formatDateTime, formatMs, formatNumber } from './format';

/**
 * admin「行動分析 → 日次レポート」タブ。
 * 左に日付リスト、右に前日ぶんの集計タイルと gpt-6-astra の所見（Markdown）を出す。
 * 数値はサーバーが集計した metrics をそのまま表示し、ここでは再計算しない。
 */

type BadgeVariant = 'success' | 'destructive' | 'warning' | 'secondary';

const STATUS_META: Record<BehaviorDailyReportStatus, { label: string; variant: BadgeVariant }> = {
  ready: { label: '完了', variant: 'success' },
  failed: { label: '失敗', variant: 'destructive' },
  generating: { label: '生成中', variant: 'warning' },
  pending: { label: '未生成', variant: 'secondary' },
};

/** generating の間だけ再取得する間隔と、諦めるまでの上限 */
const POLL_INTERVAL_MS = 5000;
const POLL_MAX_MS = 5 * 60 * 1000;

/** 小数第 1 位までに丸める（7日平均の合算表示用） */
const round1 = (value: number): number => Math.round(value * 10) / 10;

const DailyReportTab: React.FC = () => {
  const [summaries, setSummaries] = useState<BehaviorDailyReportSummary[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [report, setReport] = useState<BehaviorDailyReport | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [regenerating, setRegenerating] = useState(false);
  // ポーリング上限に達したか（Job が落ちて generating のまま固まった場合の逃げ道）
  const [pollTimedOut, setPollTimedOut] = useState(false);

  /** 詳細を取得したら一覧の該当行も同じ状態に揃える（バッジがずれないように） */
  const syncSummary = useCallback((next: BehaviorDailyReport): void => {
    setSummaries((prev) =>
      prev.map((item) =>
        item.report_date === next.report_date
          ? {
              ...item,
              status: next.status,
              model: next.model,
              generated_at: next.generated_at,
              error: next.error,
              has_narrative: Boolean(next.narrative),
            }
          : item
      )
    );
  }, []);

  useEffect(() => {
    let cancelled = false;
    setListLoading(true);
    setListError(null);
    adminBehaviorService
      .getDailyReports()
      .then((result) => {
        if (cancelled) return;
        setSummaries(result);
        setSelectedDate(result.length > 0 ? result[0].report_date : null);
      })
      .catch(() => {
        if (!cancelled) setListError('日次レポートの取得に失敗しました');
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedDate) {
      setReport(null);
      return undefined;
    }
    let cancelled = false;
    setReportLoading(true);
    setReportError(null);
    setActionError(null);
    adminBehaviorService
      .getDailyReport(selectedDate)
      .then((result) => {
        if (cancelled) return;
        setReport(result);
        syncSummary(result);
      })
      .catch(() => {
        if (!cancelled) setReportError('レポートの取得に失敗しました');
      })
      .finally(() => {
        if (!cancelled) setReportLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedDate, syncSummary]);

  const reportDate = report ? report.report_date : null;
  const reportStatus = report ? report.status : null;

  // generating の間だけポーリング。ready/failed になれば deps が変わって停止する
  useEffect(() => {
    if (!reportDate || reportStatus !== 'generating') return undefined;
    setPollTimedOut(false);
    const startedAt = Date.now();
    const timer = setInterval(() => {
      if (Date.now() - startedAt > POLL_MAX_MS) {
        // 上限を過ぎたら監視をやめ、手動の再生成を解禁する（backend は 15 分で受け付ける）
        clearInterval(timer);
        setPollTimedOut(true);
        return;
      }
      adminBehaviorService
        .getDailyReport(reportDate)
        .then((next) => {
          setReport(next);
          syncSummary(next);
        })
        .catch(() => undefined);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [reportDate, reportStatus, syncSummary]);

  const handleRegenerate = async (): Promise<void> => {
    if (!selectedDate) return;
    setRegenerating(true);
    setActionError(null);
    try {
      const next = await adminBehaviorService.regenerateDailyReport(selectedDate);
      setPollTimedOut(false);
      setReport(next);
      syncSummary(next);
    } catch {
      setActionError('再生成を開始できませんでした（すでに生成中の可能性があります）');
    } finally {
      setRegenerating(false);
    }
  };

  if (listLoading) {
    return (
      <div className="flex justify-center py-12">
        <TaskySpinner />
      </div>
    );
  }
  if (listError) {
    return <TaskyErrorState title={listError} />;
  }
  if (summaries.length === 0) {
    return (
      <TaskyEmptyState
        title="日次レポートはまだありません"
        description="毎朝 6:30 に前日分を自動生成します"
      />
    );
  }

  const metrics = report ? report.metrics : null;
  const isGenerating = reportStatus === 'generating';

  return (
    <div className="flex flex-col gap-4 md:flex-row">
      <div className="shrink-0 md:w-56">
        <h3 className="mb-2 text-sm font-semibold text-dark">日付</h3>
        <div className="flex gap-2 overflow-x-auto md:max-h-[32rem] md:flex-col md:overflow-y-auto">
          {summaries.map((item) => {
            const meta = STATUS_META[item.status];
            const selected = item.report_date === selectedDate;
            return (
              <button
                key={item.report_date}
                type="button"
                onClick={() => setSelectedDate(item.report_date)}
                aria-current={selected ? 'date' : undefined}
                className={`flex shrink-0 items-center justify-between gap-2 rounded-tasky-sm border px-3 py-2 text-left ${
                  selected ? 'border-primary bg-primary/5' : 'border-warm-border hover:bg-warm-hover'
                }`}
              >
                <span className="font-mono text-xs text-dark">{item.report_date}</span>
                <TaskyBadge variant={meta.variant}>{meta.label}</TaskyBadge>
              </button>
            );
          })}
        </div>
      </div>

      <div className="min-w-0 flex-1">
        {reportLoading && (
          <div className="flex justify-center py-12">
            <TaskySpinner />
          </div>
        )}

        {!reportLoading && reportError && <TaskyErrorState title={reportError} />}

        {!reportLoading && !reportError && report && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <h3 className="text-base font-semibold text-dark">{report.report_date} の日次レポート</h3>
                <p className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-warm-text-muted">
                  <span>{report.model ?? 'モデル未記録'}</span>
                  <span>生成 {formatDateTime(report.generated_at)}</span>
                  <span>
                    入力 {formatNumber(report.input_tokens)} / 出力 {formatNumber(report.output_tokens)} tokens
                  </span>
                </p>
              </div>
              <TaskyButton
                variant="secondary"
                size="sm"
                onClick={handleRegenerate}
                disabled={regenerating || (isGenerating && !pollTimedOut)}
              >
                {isGenerating && !pollTimedOut ? '生成中…' : '再生成'}
              </TaskyButton>
            </div>

            {report.status === 'failed' && report.error && (
              <TaskyErrorState title="生成に失敗しました" description={report.error} />
            )}
            {actionError && <TaskyErrorState title={actionError} />}
            {isGenerating && pollTimedOut && (
              <TaskyAlert tone="warning">
                生成に時間がかかっています。しばらく待ってから再生成してください
              </TaskyAlert>
            )}

            {metrics ? (
              <>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  <TaskyStatCard
                    label="セッション"
                    value={formatNumber(metrics.totals.sessions)}
                    delta={`7日平均 ${formatNumber(metrics.totals_7d_avg.sessions)}`}
                    icon={<Activity />}
                    iconTone="primary"
                  />
                  <TaskyStatCard
                    label="ユーザー"
                    value={formatNumber(metrics.totals.users)}
                    delta={`7日平均 ${formatNumber(metrics.totals_7d_avg.users)}`}
                    icon={<Users />}
                    iconTone="success"
                  />
                  <TaskyStatCard
                    label="ページビュー"
                    value={formatNumber(metrics.totals.page_views)}
                    delta={`7日平均 ${formatNumber(metrics.totals_7d_avg.page_views)}`}
                    icon={<Eye />}
                    iconTone="purple"
                  />
                  <TaskyStatCard
                    label="エラー"
                    value={formatNumber(metrics.totals.api_errors + metrics.totals.js_errors)}
                    delta={`7日平均 ${formatNumber(
                      round1(metrics.totals_7d_avg.api_errors + metrics.totals_7d_avg.js_errors)
                    )}`}
                    deltaTone={metrics.totals.api_errors + metrics.totals.js_errors > 0 ? 'down' : 'neutral'}
                    icon={<AlertTriangle />}
                    iconTone="warning"
                  />
                </div>

                <div className="rounded-tasky-md border border-warm-border p-3">
                  <h4 className="mb-2 text-xs font-semibold text-dark">生成フロー（セッション数）</h4>
                  <dl className="grid grid-cols-3 gap-2 text-xs sm:grid-cols-5">
                    {[
                      { label: '開始', value: metrics.generation.started_sessions },
                      { label: '完了', value: metrics.generation.completed_sessions },
                      { label: '失敗', value: metrics.generation.failed_sessions },
                      { label: 'ダウンロード', value: metrics.generation.dl_sessions },
                      { label: '結果なし', value: metrics.generation.started_no_result },
                    ].map((item) => (
                      <div key={item.label}>
                        <dt className="text-warm-text-muted">{item.label}</dt>
                        <dd className="text-sm font-semibold text-dark">{formatNumber(item.value)}</dd>
                      </div>
                    ))}
                  </dl>
                </div>

                <div className="rounded-tasky-md border border-warm-border p-3">
                  <h4 className="mb-2 text-xs font-semibold text-dark">セッションの内訳</h4>
                  <dl className="grid grid-cols-3 gap-2 text-xs sm:grid-cols-5">
                    {[
                      { label: 'ログイン済み', value: formatNumber(metrics.session_stats.with_user) },
                      { label: '1ページのみ', value: formatNumber(metrics.session_stats.single_pv) },
                      { label: '10秒未満', value: formatNumber(metrics.session_stats.under_10s) },
                      { label: '平均PV', value: formatNumber(metrics.session_stats.avg_pv) },
                      { label: '平均滞在', value: formatMs(metrics.session_stats.avg_dur_ms) },
                    ].map((item) => (
                      <div key={item.label}>
                        <dt className="text-warm-text-muted">{item.label}</dt>
                        <dd className="text-sm font-semibold text-dark">{item.value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </>
            ) : (
              <p className="text-sm text-warm-text-muted">この日の集計値はまだありません。</p>
            )}

            {report.narrative ? (
              <div className="rounded-tasky-md border border-warm-border p-4 text-sm text-dark [&_h2]:mt-4 [&_h2]:text-base [&_h2]:font-semibold [&_h3]:mt-3 [&_h3]:text-sm [&_h3]:font-semibold [&_li]:ml-4 [&_li]:list-disc [&_p]:my-2 [&_table]:text-xs [&_td]:border [&_td]:px-2 [&_th]:border [&_th]:px-2 [&_ul]:my-2">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{report.narrative}</ReactMarkdown>
              </div>
            ) : (
              !isGenerating && (
                <p className="text-sm text-warm-text-muted">
                  所見はまだありません。「再生成」で作り直せます。
                </p>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DailyReportTab;
